<script>
  import { page, router } from '@inertiajs/svelte';
  import { menuOpenState, activePathState } from '@/js/utils/stores.js';
  import LangSwitch from '../LangSwitch.svelte';
  import HeaderMenuLeft from './Left.svelte';
  import HeaderMenuRight from './Right.svelte';
  import Burger from '$lib/Burger.svelte';
  import viewport from '@/js/utils/useViewportAction.js';

  $: headerMenu = $page.props.header_menu;

  // Handle menu animation.
  let open;
  menuOpenState.subscribe((value) => {
    open = value;

    var htmlMain = document.documentElement;
    if (open) {
      htmlMain.classList.add('no-scroll');
    } else {
      htmlMain.classList.remove('no-scroll');
    }
  });

  function toggleMenuState() {
    menuOpenState.update((open) => !open);
  }

  function closeMenu(event) {
    if (event.key == 'Escape') {
      menuOpenState.set((open = false));
    }
  }

  let activePath;
  activePathState.subscribe((value) => {
    activePath = value;
  });

  // Handle route change.
  if (!import.meta.env.SSR) {
    router.on('start', (event) => {
      if (event.detail.visit.url.pathname !== router.page.url) {
        document.documentElement.classList.remove('no-scroll');
        menuOpenState.update((open) => false);
        activePathState.set(event.detail.visit.url.pathname);
      }
    });
  }

  let grow = false;
  let growHorizontal = false;
</script>

<svelte:window on:keydown={closeMenu} />

<div class="menu gradient--royal" class:open>
  <div class="menu__bg-wrap">
    <div class="menu__bg menu__bg-visual" />
    <div class="menu__bg menu__bg-noise" />
  </div>
  <div class="menu__top">
    <div class="container container--large">
      <div class="menu-top">
        <LangSwitch />

        <Burger theme="light" {open} on:click={toggleMenuState} />
      </div>
    </div>
  </div>

  <div class="menu__main">
    <div class="container">
      <div class="menu-main">
        <div class="menu-main__left">
          <HeaderMenuLeft data={headerMenu.left} />
          <div
            class="line-horizontal"
            class:grow-horizontal={growHorizontal}
            use:viewport
            on:enterViewport={() => (growHorizontal = true)}
          />
        </div>
        <div
          class="menu-main__line line-vertical"
          class:grow
          use:viewport
          on:enterViewport={() => (grow = true)}
        />

        <div class="menu-main__right">
          <HeaderMenuRight data={headerMenu.right} />
          <div
            class="line-horizontal"
            class:grow-horizontal={growHorizontal}
            use:viewport
            on:enterViewport={() => (growHorizontal = true)}
          />
        </div>
      </div>
    </div>
  </div>

  <div class="menu__bottom">
    <div class="container container--large">
      <div class="menu-bottom">
        {#if headerMenu.contacts?.phone}
          <a
            class="link link--alt link--icon"
            href={`tel:${headerMenu.contacts.phone}`}
          >
            <i class="isax isax-call5" />
            <span>{headerMenu.contacts.phone}</span>
          </a>
        {/if}

        {#if headerMenu.contacts?.email}
          <a
            class="link link--alt link--icon"
            href={`mailto:${headerMenu.contacts.email}`}
          >
            <i class="isax isax-arrow-right-3" />
            <span>{headerMenu.contacts.email}</span>
          </a>
        {/if}
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  @keyframes grow {
    0% {
      height: 0%;
      top: 50%;
    }

    90% {
      height: 110%;
      top: -5%;
    }

    100% {
      height: 100%;
      top: 0%;
    }
  }

  @keyframes grow-horizontal {
    0% {
      width: 0%;
      left: 50%;
    }

    90% {
      width: 110%;
      left: -5%;
    }

    100% {
      width: 100%;
      left: 0%;
    }
  }
  .menu {
    position: absolute;
    z-index: 100;
    top: 0;
    left: 0;
    right: 0;

    color: var(--text-alt);
    background-color: var(--royal);

    transform: translateY(-100%);
    will-change: transform;
    transition: all 0.25s;

    .container {
      @include resp-max('m') {
        width: 90%;
      }
    }

    &__bg {
      position: absolute;
      z-index: -1;
      inset: 0;

      &-visual {
        background: {
          image: url(img/brand-bg-el.svg);
          size: contain;
          position: center;
          repeat: no-repeat;
        }

        opacity: 0.01;
      }

      &-noise {
        background-image: url(img/noise.png);
      }
    }

    &__main {
      padding: 10rem 0 16rem;

      @include resp-max('m') {
        padding: 0 0 10rem;
      }
    }

    &.open {
      transform: translateY(0);
    }
  }

  .menu-top,
  .menu-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .menu-top {
    padding: 4.5rem 0;

    @include resp-max('s') {
      padding: 1.5rem 0 0;
    }

    :global {
      .burger {
        opacity: 0;
        transition: all 0.25s;
        transition-delay: 0.25s;

        &__inner {
          transition-delay: 0.25s;
        }

        .open & {
          opacity: 1;
        }
      }
    }
  }

  .menu-main {
    @include resp-min('s') {
      display: flex;
    }
    position: relative;
    gap: 8rem;

    &__left {
      position: relative;

      @include resp-range('s', 'm') {
        width: 58%;
      }
      @include resp-min('m') {
        width: 42%;
      }
      @include resp-max('m') {
        padding-right: 5.7rem;
        padding-bottom: 3rem;
      }
      @include resp-min('m') {
        padding-right: 8rem;
      }
    }

    &__line {
      @include resp-max('m') {
        display: none;
      }

      position: absolute;
      top: 0;
      bottom: 0;
      left: 50%;

      &.grow {
        animation: grow ease-in-out 1s;
      }
    }

    &__right {
      position: relative;

      @include resp-max('m') {
        margin-top: 3rem;
        padding-bottom: 3rem;
      }

      @include resp-min('m') {
        padding: 0 10rem;
      }
    }

    .line-horizontal {
      @include resp-min('s') {
        display: none;
      }

      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      display: block;
      height: 1px;

      &.grow-horizontal {
        animation: grow-horizontal ease-in-out 1s;
      }
    }
  }

  .menu-bottom {
    padding-bottom: 5rem;
  }
</style>
